import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Search,
  Users,
  MapPin,
  Briefcase,
  Star,
  Filter,
  ChevronRight,
  Video,
  FileText,
  MessageSquare,
  CheckCircle,
  Clock,
  User,
  Award
} from "lucide-react";

const CandidateSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterJob, setFilterJob] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    fetchApplicants();
    fetchJobs();
  }, []);

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/candidates/applicants/all`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setApplicants(data.applicants || []);
      }
    } catch (error) {
      console.error("Failed to fetch applicants:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/recruiter`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const handleSendMessage = async (applicantId, applicantName) => {
    // Navigate to messages with this candidate
    navigate(`/messages?to=${applicantId}&name=${encodeURIComponent(applicantName)}`);
  };

  const handleInviteInterview = async (applicantId, applicantName, jobId) => {
    try {
      const response = await fetch(`${API}/ai/interview/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          candidate_id: applicantId,
          job_id: jobId,
          num_questions: 6,
          time_limit_minutes: 30
        }),
      });

      if (response.ok) {
        toast.success(`Interview invitation sent to ${applicantName}!`);
        fetchApplicants(); // Refresh to show updated status
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send interview invitation");
    }
  };

  // Filter applicants
  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = !searchQuery || 
      applicant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.headline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.skills?.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesJob = !filterJob || applicant.job_id === filterJob;
    const matchesStatus = !filterStatus || applicant.application_status === filterStatus;
    
    return matchesSearch && matchesJob && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />

      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-bold">FIND CANDIDATES</h1>
            <p className="text-gray-400">View and manage applicants for your job postings</p>
          </div>

          {/* Filters */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-4 mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, headline, or skills..."
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 pl-10"
                  data-testid="search-input"
                />
              </div>
              
              {/* Job Filter */}
              <select
                value={filterJob}
                onChange={(e) => setFilterJob(e.target.value)}
                className="px-4 py-2 bg-obsidian border border-[#333] text-white"
                data-testid="filter-job"
              >
                <option value="">All Jobs</option>
                {jobs.map(job => (
                  <option key={job.job_id} value={job.job_id}>{job.title}</option>
                ))}
              </select>
              
              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-obsidian border border-[#333] text-white"
                data-testid="filter-status"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="offered">Offered</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </motion.div>

          {/* Results Summary */}
          <div className="mb-4 text-gray-400 text-sm">
            Showing {filteredApplicants.length} applicants {filterJob && `for ${jobs.find(j => j.job_id === filterJob)?.title || 'selected job'}`}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredApplicants.length === 0 ? (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-12 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl font-bold mb-2">No Applicants Found</h3>
              <p className="text-gray-400 mb-6">
                {searchQuery || filterJob || filterStatus
                  ? "Try adjusting your filters to see more candidates"
                  : "Post jobs to receive applications from candidates"}
              </p>
              {!searchQuery && !filterJob && !filterStatus && (
                <Button
                  onClick={() => navigate("/jobs/create")}
                  className="bg-crimson hover:bg-crimson/90"
                >
                  Post a Job
                </Button>
              )}
            </motion.div>
          ) : (
            <div className="space-y-4">
              {filteredApplicants.map((applicant, i) => (
                <motion.div
                  key={`${applicant.user_id}-${applicant.job_id}`}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-[#333] transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`applicant-${applicant.user_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    {/* Candidate Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-crimson/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {applicant.picture ? (
                          <img src={applicant.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-crimson" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-white">
                          {applicant.name || "Anonymous"}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">
                          {applicant.headline || "Job Seeker"}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          {applicant.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" /> {applicant.location}
                            </span>
                          )}
                          {applicant.experience_years > 0 && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" /> {applicant.experience_years} years exp
                            </span>
                          )}
                          {applicant.match_score && (
                            <span className="flex items-center gap-1 text-green-400">
                              <Star className="w-4 h-4" /> {applicant.match_score}% match
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Job & Status */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Applied for:</span>
                        <Link 
                          to={`/jobs/${applicant.job_id}`}
                          className="text-crimson hover:underline text-sm"
                        >
                          {applicant.job_title}
                        </Link>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs uppercase ${
                          applicant.application_status === "offered" ? "bg-green-500/20 text-green-400" :
                          applicant.application_status === "interviewed" ? "bg-blue-500/20 text-blue-400" :
                          applicant.application_status === "reviewed" ? "bg-yellow-500/20 text-yellow-400" :
                          applicant.application_status === "rejected" ? "bg-red-500/20 text-red-400" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>
                          {applicant.application_status || "Applied"}
                        </span>
                        {applicant.has_interview && (
                          <span className={`px-2 py-1 text-xs flex items-center gap-1 ${
                            applicant.interview_status === "completed" ? "bg-green-500/20 text-green-400" :
                            "bg-blue-500/20 text-blue-400"
                          }`}>
                            <Video className="w-3 h-3" />
                            {applicant.interview_status === "completed" ? "Interview Done" : "Interview Sent"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {applicant.skills?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {applicant.skills.slice(0, 6).map((skill) => (
                        <span key={skill} className="px-2 py-1 bg-obsidian border border-[#333] text-xs text-gray-300">
                          {skill}
                        </span>
                      ))}
                      {applicant.skills.length > 6 && (
                        <span className="px-2 py-1 text-xs text-gray-500">
                          +{applicant.skills.length - 6} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-[#333]">
                    <Button
                      onClick={() => navigate(`/candidates/${applicant.user_id}`)}
                      variant="outline"
                      className="border-[#333] text-white hover:bg-white/5"
                      data-testid={`view-profile-${applicant.user_id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" /> View Profile
                    </Button>
                    <Button
                      onClick={() => handleSendMessage(applicant.user_id, applicant.name)}
                      variant="outline"
                      className="border-[#333] text-white hover:bg-white/5"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" /> Message
                    </Button>
                    {!applicant.has_interview && (
                      <Button
                        onClick={() => handleInviteInterview(applicant.user_id, applicant.name, applicant.job_id)}
                        className="bg-crimson hover:bg-crimson/90 text-white"
                      >
                        <Video className="w-4 h-4 mr-2" /> Invite to Interview
                      </Button>
                    )}
                    {applicant.has_interview && applicant.interview_status === "completed" && (
                      <Button
                        onClick={() => navigate(`/interviews/${applicant.interview_session_id}`)}
                        variant="outline"
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> View Interview
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CandidateSearch;
